import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useHasRole } from '@/components/auth/RoleGuard';

type InactiveReason = 'TRIAL_EXPIRED' | 'PAST_DUE' | 'CANCELED';

const COPY: Record<InactiveReason, { title: string; body: string; cta: string }> = {
  TRIAL_EXPIRED: {
    title: 'Seu período de teste terminou',
    body: 'Escolha um plano para voltar a usar o soupelvi. Seus dados continuam guardados.',
    cta: 'Escolher um plano',
  },
  PAST_DUE: {
    title: 'Há um pagamento pendente',
    body: 'Regularize o pagamento da assinatura para recuperar o acesso. Seus dados continuam guardados.',
    cta: 'Regularizar pagamento',
  },
  CANCELED: {
    title: 'A assinatura foi cancelada',
    body: 'Reative a assinatura para voltar a usar o soupelvi. Seus dados continuam guardados.',
    cta: 'Reativar assinatura',
  },
};

export function getInactiveReason(
  planStatus: string | undefined,
  isTrialExpired: boolean,
): InactiveReason {
  if (isTrialExpired) return 'TRIAL_EXPIRED';
  if (planStatus === 'PAST_DUE') return 'PAST_DUE';
  return 'CANCELED';
}

/** Tela exibida no lugar das páginas quando a assinatura da clínica está inativa. */
export function SubscriptionInactive() {
  const { planStatus, isTrialExpired } = useSubscription();
  const isAdmin = useHasRole('ADMIN');
  const copy = COPY[getInactiveReason(planStatus, isTrialExpired)];

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="max-w-md text-center space-y-4" role="alert">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <h1 className="text-xl font-semibold">{copy.title}</h1>
        <p className="text-sm text-muted-foreground">{copy.body}</p>
        {isAdmin ? (
          <Button asChild>
            <Link to="/settings">{copy.cta}</Link>
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Fale com o administrador da clínica para resolver.
          </p>
        )}
      </div>
    </div>
  );
}
